/**
 * Server clock sync — measures the offset between the server's clock and
 * the browser's clock so timers derived from server timestamps
 * (`last_click_at`) don't jump when the two clocks disagree.
 *
 * Bug this fixes:
 *   - Click +1. Server stores `last_click_at = server_now_UTC`.
 *   - Browser polls 30 s later; receives the response. If the server
 *     clock is 14 s BEHIND the client's, the naïve
 *     `elapsed = Date.now() - Date.parse(last_click_at)` reads 14 s
 *     immediately — visible to the user as "14s ago" seconds after
 *     they clicked. Symmetric error in the opposite direction if the
 *     server clock is ahead: elapsed can go NEGATIVE.
 *
 * Fix:
 *   - Every axios response is inspected for the `Date` header
 *     (a CORS-safelisted response header, so browsers always expose
 *     it — no server-side allow-list needed).
 *   - We estimate the network's one-way transit time as half the
 *     request→response wall clock; the "reference server time" is
 *     `Date(header) + transit/2`. The offset is
 *     `serverReferenceTime - clientReceiveTime`.
 *   - Callers convert server timestamps into client-clock equivalents
 *     via `serverToClientMs(serverMs)` before computing elapsed times.
 *     Everything downstream then works in a single (client) clock
 *     domain.
 *
 * Trade-offs:
 *   - The `Date` HTTP header has 1-second resolution, so we can't
 *     drive sub-second precision. A ±1 s wobble is acceptable for a
 *     "seconds ago" label.
 *   - We keep a running exponential average of the offset with a
 *     tight weight on the latest measurement so a temporary network
 *     hiccup doesn't skew the label for minutes.
 */

// Offset in ms such that: serverTime ≈ clientTime + offset.
// Equivalent conversion: clientTimeForServerEvent = serverEventMs - offset.
let _offsetMs = 0;
let _haveSample = false;

/**
 * Called from an axios response interceptor with the request timings.
 * `requestStartMs` is `performance.now()`-corrected `Date.now()` from
 * before the request was sent; if not available, pass `undefined` and
 * we'll estimate transit as 0 (still an improvement over no offset).
 */
export function ingestServerDateHeader(dateHeader, requestStartMs) {
  if (!dateHeader) return;
  const serverMs = Date.parse(dateHeader);
  if (!Number.isFinite(serverMs)) return;

  const clientReceiveMs = Date.now();
  // Best-effort one-way transit ≈ half the round-trip time.
  const transitMs =
    typeof requestStartMs === "number"
      ? Math.max(0, (clientReceiveMs - requestStartMs) / 2)
      : 0;
  const serverReferenceMs = serverMs + transitMs;
  const sample = serverReferenceMs - clientReceiveMs;

  // First sample wins outright; subsequent samples blend at 40% so a
  // burst of NTP-corrected packets can update quickly but a single
  // outlier can't yank the label.
  _offsetMs = _haveSample ? _offsetMs * 0.6 + sample * 0.4 : sample;
  _haveSample = true;
}

/** Current best estimate of `serverTime - clientTime` in ms. */
export function getServerOffsetMs() {
  return _offsetMs;
}

/**
 * Convert a server-clock timestamp (e.g. Date.parse(last_click_at) from
 * the API) to its client-clock equivalent. Use this once at the
 * boundary; everything downstream works in the client clock domain and
 * can compare against Date.now() safely.
 */
export function serverToClientMs(serverMs) {
  if (!Number.isFinite(serverMs)) return 0;
  return serverMs - _offsetMs;
}

/** Test-only reset. */
export function _resetClockSync() {
  _offsetMs = 0;
  _haveSample = false;
}
